"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

const THEME_COLORS = [
  { name: "Cyan", value: "#00BCD4" },
  { name: "Blue", value: "#2196F3" },
  { name: "Green", value: "#388E3C" },
  { name: "Teal", value: "#009688" },
  { name: "Lime", value: "#CDDC39" },
  { name: "Orange", value: "#FF9800" },
] as const;

const DEFAULT_COLOR = "#388E3C";
const STORAGE_KEY = "theme-color";

interface ThemeColorContextType {
  themeColor: string;
  setThemeColor: (color: string) => void;
  colors: typeof THEME_COLORS;
}

const ThemeColorContext = createContext<ThemeColorContextType | null>(null);

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

function darken(hex: string, amount: number) {
  const { r, g, b } = hexToRgb(hex);
  const d = 1 - amount;
  const dr = Math.round(r * d);
  const dg = Math.round(g * d);
  const db = Math.round(b * d);
  return `#${dr.toString(16).padStart(2, "0")}${dg.toString(16).padStart(2, "0")}${db.toString(16).padStart(2, "0")}`;
}

function isLightColor(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  return r * 0.299 + g * 0.587 + b * 0.114 > 186;
}

function applyThemeColor(color: string) {
  const root = document.documentElement;
  const hoverColor = darken(color, 0.15);
  const activeColor = darken(color, 0.25);
  const foreground = isLightColor(color) ? "#000000" : "#ffffff";

  root.style.setProperty("--primary", color);
  root.style.setProperty("--primary-foreground", foreground);
  root.style.setProperty("--ring", color);
  root.style.setProperty("--sidebar-primary", color);
  root.style.setProperty("--sidebar-ring", color);
  root.style.setProperty("--theme-color", color);
  root.style.setProperty("--theme-color-hover", hoverColor);
  root.style.setProperty("--theme-color-active", activeColor);
  root.style.setProperty("--theme-color-20", color + "33");
}

export function ThemeColorProvider({ children }: { children: ReactNode }) {
  const [themeColor, setThemeColorState] = useState(DEFAULT_COLOR);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setThemeColorState(saved);
      applyThemeColor(saved);
    }
  }, []);

  const setThemeColor = (color: string) => {
    setThemeColorState(color);
    localStorage.setItem(STORAGE_KEY, color);
    applyThemeColor(color);
  };

  return (
    <ThemeColorContext.Provider value={{ themeColor, setThemeColor, colors: THEME_COLORS }}>
      {children}
    </ThemeColorContext.Provider>
  );
}

export function useThemeColor() {
  const ctx = useContext(ThemeColorContext);
  if (!ctx) throw new Error("useThemeColor must be used within ThemeColorProvider");
  return ctx;
}
